<!-- Единая форма расхода: создание, редактирование, связи, оплата и файлы. -->
<template>
  <base-loader v-if="dataIsLoading" />

  <form
    v-else
    :id="formId"
    @submit.prevent="handleSubmit"
    class="expense-form"
    ref="formBodyRef"
  >
    <!-- Секции отвечают за поля, родитель хранит данные, валидацию и submit -->
    <ExpenseConfirmationSection
      v-if="isEditMode"
      :accepter-name="userName(expense.accepter_id)"
      :expense="expense"
      :is-submitting="isSubmitting"
      :source-type-label="sourceTypeLabel"
      @accept="postAcceptRequest"
    />

    <ExpenseMainSection
      :errors="errors"
      :expense="expense"
      :show-liters-field="showLitersField"
      @category-change="handleCategoryChange"
      @clear-error="clearError"
      @update-field="updateExpenseField"
    />

    <ExpensePaymentSection
      :expense="expense"
      :is-payment-source-creating="isPaymentSourceCreating"
      :show-inline-payment-source-form="showInlinePaymentSourceForm"
      @close-inline-payment-source-form="
        showInlinePaymentSourceForm = false
      "
      @save-inline-payment-source="saveInlinePaymentSource"
      @toggle-inline-payment-source-form="toggleInlinePaymentSourceForm"
      @update-field="updateExpenseField"
    />

    <ExpenseCommentSection
      :expense="expense"
      @update-field="updateExpenseField"
    />

    <ExpenseLinksSection
      :expense="expense"
      @fleet-park-change="handleFleetParkChange"
      @update-field="updateExpenseField"
      @vehicle-change="handleVehicleChange"
    />

    <ExpenseFilesSection
      v-model:attachment-ids="expense.attachment_ids"
      :form-id="formId"
      :existing-attachments="existingAttachments"
    />
  </form>
</template>

<script>
import { useNotificationStore } from "@/stores/notificationStore";
import { mapAssociationList } from "@/utils/associationListUtils";
import { useAttachmentStore } from "@/stores/attachmentStore";
import { useCompanyUserStore } from "@/stores/companyUserStore";
import { useExpenseCategoryStore } from "@/stores/expenseCategoryStore";
import { useExpenseStore } from "@/stores/expenseStore";
import { usePaymentSourceStore } from "@/stores/paymentSourceStore";

import { getUserId } from "@/utils/userUtils";
import { isImage, resolveUrl } from "@/utils/fileUtils";
import { currentDatetime } from "@/utils/dateUtils";
import { roundToTwoDecimals } from "@/utils/numberUtils";

import ExpenseCommentSection from "@/components/Expense/sections/CommentSection.vue";
import ExpenseConfirmationSection from "@/components/Expense/sections/ConfirmationSection.vue";
import ExpenseFilesSection from "@/components/Expense/sections/FilesSection.vue";
import ExpenseLinksSection from "@/components/Expense/sections/LinksSection.vue";
import ExpenseMainSection from "@/components/Expense/sections/MainSection.vue";
import ExpensePaymentSection from "@/components/Expense/sections/PaymentSection.vue";

export default {
  name: "ExpenseForm",

  components: {
    ExpenseCommentSection,
    ExpenseConfirmationSection,
    ExpenseFilesSection,
    ExpenseLinksSection,
    ExpenseMainSection,
    ExpensePaymentSection,
  },

  props: {
    expenseId: {
      type: [String, Number],
      default: null,
    },
    tripId: {
      type: Number,
      default: null,
    },
    formId: {
      type: String,
      default: "expense-form",
    },
  },

  emits: [
    "close",
    "expense-created",
    "expense-updated",
    "expense-deleted",
    "expense-accepted",
    "submitting-change",
  ],

  data() {
    return {
      expense: {
        id: null,
        amount: null,
        comment: "",
        spent_at: null,
        expense_category_id: null,
        liters: null,
        accepter_id: null,
        accepted_at: null,
        spender_id: null,
        trip_id: null,
        waybill_id: null,
        fleet_park_id: null,
        vehicle_id: null,
        fleet_repair_id: null,
        payment_source_id: null,
        counterparty_id: null,
        source_type: null,
        attachment_ids: [],
      },

      dataIsLoading: true,
      existingAttachments: [],
      showLitersField: false,

      showInlinePaymentSourceForm: false,
      isPaymentSourceCreating: false,

      errors: {
        expense_category_id: false,
        amount: false,
      },
    };
  },

  watch: {
    tripId: {
      handler(value) {
        if (!this.isEditMode) {
          this.expense.trip_id = value;
        }
      },
      immediate: true,
    },

    isSubmitting(value) {
      this.$emit("submitting-change", value);
    },
  },

  computed: {
    isEditMode() {
      return Boolean(this.expenseId);
    },

    attachmentStore() {
      return useAttachmentStore();
    },

    companyUserStore() {
      return useCompanyUserStore();
    },

    expenseStore() {
      return useExpenseStore();
    },

    isSubmitting() {
      return (
        this.expenseStore.isSubmitting || this.isPaymentSourceCreating
      );
    },
  },

  methods: {
    sourceTypeLabel(type) {
      const labels = {
        manual: "Создан вручную",
        bank_statement: "Импортирован из банковских выписок",
      };
      return labels[type] || type;
    },

    validateForm() {
      let isValid = true;
      this.errors = {
        expense_category_id: false,
        amount: false,
      };

      if (!this.isFieldFilled("expense_category_id")) {
        this.errors.expense_category_id = true;
        isValid = false;
      }
      if (!this.isFieldFilled("amount")) {
        this.errors.amount = true;
        isValid = false;
      }

      return isValid;
    },

    isFieldFilled(field) {
      const value = this.expense[field];
      return value !== null && value !== undefined && value !== "";
    },

    updateExpenseField(field, value) {
      this.expense[field] = value;
    },

    clearError(field) {
      if (!Object.prototype.hasOwnProperty.call(this.errors, field)) {
        return;
      }

      this.errors[field] = false;
    },

    handleCategoryChange(categoryId) {
      const store = useExpenseCategoryStore();
      const id = categoryId ?? this.expense.expense_category_id;
      const selectedCategory =
        store.selectItems.find((cat) => cat.id == id) ||
        store.expenseCategories.find((cat) => cat.id == id);
      this.showLitersField =
        selectedCategory?.requires_liters === true;
      if (!this.showLitersField) this.expense.liters = null;
    },

    userName(userId) {
      const user = this.companyUserStore.companyUsers.find(
        (u) => u.id === userId
      );
      return user ? user.user_initials || user.user?.email : "-";
    },

    toggleInlinePaymentSourceForm() {
      this.showInlinePaymentSourceForm =
        !this.showInlinePaymentSourceForm;
    },

    async saveInlinePaymentSource(formData) {
      this.isPaymentSourceCreating = true;
      try {
        const store = usePaymentSourceStore();
        const created = await store.createSource(formData);

        this.expense.payment_source_id = created.id;
        this.showInlinePaymentSourceForm = false;

        const notificationStore = useNotificationStore();
        notificationStore.showSuccess(
          "Источник оплаты создан и выбран"
        );
      } finally {
        this.isPaymentSourceCreating = false;
      }
    },

    async getCompanyUsersRequest() {
      await this.companyUserStore.fetchCompanyUsers();
    },

    async loadFormData() {
      this.dataIsLoading = true;
      try {
        const store = useExpenseCategoryStore();

        await Promise.all([
          store.fetchExpenseCategories(),
          this.getCompanyUsersRequest(),
        ]);

        if (!this.isEditMode) return;

        const result = await this.expenseStore.fetchExpense(
          this.expenseId
        );

        this.expense = {
          id: result.id,
          expense_category_id: result.expense_category_id,
          amount: result.amount,
          liters: result.liters,
          comment: result.comment,
          accepter_id: result.accepter_id,
          accepted_at: result.accepted_at,
          spender_id: result.spender_id,
          spent_at: result.spent_at,
          trip_id: result.trip_id,
          waybill_id: result.waybill_id,
          fleet_park_id: result.vehicle?.fleet_park_id || null,
          vehicle_id: result.vehicle_id,
          fleet_repair_id: result.fleet_repair_id,
          payment_source_id: result.payment_source_id,
          counterparty_id: result.counterparty_id,
          source_type: result.source_type,
          attachment_ids: mapAssociationList(result.attachments, (attachment) => attachment.id),
        };

        if (result.attachments?.length) {
          this.existingAttachments = await Promise.all(
            result.attachments.map(async (attData) => {
              const attachment =
                await this.attachmentStore.fetchAttachment(attData.id);
              const file = attachment.file;
              const fileUrl = attachment.file_url;
              return {
                id: attData.id,
                name: file.metadata.filename,
                file_url: fileUrl,
                size: file.metadata.size,
                mime_type: file.metadata.mime_type,
                type: file.metadata.mime_type,
                url: resolveUrl(fileUrl),
                preview: isImage({ type: file.metadata.mime_type })
                  ? resolveUrl(fileUrl)
                  : null,
              };
            })
          );
        }

        this.handleCategoryChange();
      } finally {
        this.dataIsLoading = false;
      }
    },

    async postAcceptRequest(expenseId) {
      await this.expenseStore.acceptExpense(expenseId);
      await this.loadFormData();
      this.$emit("expense-accepted", expenseId);
    },

    handleFleetParkChange() {
      this.expense.vehicle_id = null;
    },

    handleVehicleChange(option) {
      if (!option) return;

      this.expense.fleet_park_id = option?.fleet_park_id || null;
    },

    async handleSubmit() {
      const notificationStore = useNotificationStore();

      if (!this.validateForm()) {
        notificationStore.showError("Заполните обязательные поля");

        this.$nextTick(() => {
          const firstErrorElement =
            this.$refs.formBodyRef?.querySelector(
              ".invalid, .base-form-field__error"
            );

          if (firstErrorElement) {
            const formGroup =
              firstErrorElement.closest(".form-group");
            const targetElement = formGroup || firstErrorElement;

            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });

        return;
      }

      const payload = this.buildPayload();

      if (this.isEditMode) {
        const expense = await this.expenseStore.updateExpense(
          this.expense.id,
          payload
        );

        this.$emit("expense-updated", expense);
        this.$emit("close");
        return;
      }

      const expense = await this.expenseStore.createExpense(payload);

      this.$emit("expense-created", expense);
      this.$emit("close");
    },

    buildPayload() {
      return {
        expense_category_id: this.expense.expense_category_id,
        amount: String(roundToTwoDecimals(this.expense.amount)),
        liters: this.expense.liters ? String(this.expense.liters) : null,
        comment: this.expense.comment,
        spent_at: this.expense.spent_at,
        spender_id: this.expense.spender_id,
        trip_id: this.expense.trip_id,
        waybill_id: this.expense.waybill_id,
        vehicle_id: this.expense.vehicle_id,
        fleet_repair_id: this.expense.fleet_repair_id,
        payment_source_id: this.expense.payment_source_id,
        counterparty_id: this.expense.counterparty_id,
        attachment_ids: this.expense.attachment_ids,
      };
    },

    async handleDelete() {
      if (!this.isEditMode) return;

      await this.expenseStore.deleteExpense(this.expense.id);
      this.$emit("expense-deleted");
      this.$emit("close");
    },
  },

  async created() {
    this.expense = {
      ...this.expense,
      spent_at: this.expense.spent_at || currentDatetime(),
      trip_id: this.tripId,
      spender_id: this.expense.spender_id || getUserId(),
    };

    await this.loadFormData();
  },
};
</script>

<style scoped>
.expense-form {
  display: flex;
  flex-direction: column;
  gap: var(--form-section-gap, var(--base-out-indent));
}

.expense-form :deep(.form-layout__row) {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 900px) {
  .expense-form :deep(.form-layout__row) {
    grid-template-columns: 1fr;
  }
}
</style>
